import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useSnackbar } from '../../components/common/Snackbar';
import {
  filterAssignableUsers,
  hasAssignmentPrivileges,
  normalizeRole,
  normalizeRegion,
} from '../../utils/assignmentRules';
import { GLOBAL_ASSIGNMENT_ROLES, EXCLUDE_PEERS_ROLES } from '../../constants/editQueryModal';

/**
 * Hook to manage data fetching and user filtering for EditQueryModal
 * @param {Object} params - Hook parameters
 * @param {boolean} params.open - Whether modal is open
 * @param {Object} params.query - Query object
 * @param {Object} params.currentUser - Current user object
 * @returns {Object} Users data and loading state
 */
export const useEditQueryModalData = ({ open, query, currentUser }) => {
  const { showSnackbar } = useSnackbar();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [allAssignableUsers, setAllAssignableUsers] = useState([]);

  const fetchAssignableUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await api.get('/admin/users', {
        params: { limit: 200, isActive: true },
      });
      const allUsers = response.data?.data?.users || [];
      setAllAssignableUsers(allUsers);
    } catch (error) {
      // console.error('Failed to load assignable users:', error);
      const message = error.response?.data?.message || 'Failed to load assignable users';
      showSnackbar(message, 'error');
      setUsers((prev) => prev.length ? prev : []);
    } finally {
      setLoadingUsers(false);
    }
  }, [showSnackbar]);

  const applyAssignableOptions = useCallback(() => {
    if (!query) {
      setUsers([]);
      return;
    }

    let filtered = [];

    if (currentUser && hasAssignmentPrivileges(currentUser)) {
      const normalizedRole = normalizeRole(currentUser.role);
      const isGlobalAssignmentRole = GLOBAL_ASSIGNMENT_ROLES.includes(normalizedRole);
      const queryRegion = (query.region || '').trim().toUpperCase();

      if (isGlobalAssignmentRole) {
        const shouldExcludePeers = EXCLUDE_PEERS_ROLES.includes(normalizedRole);
        const queryRegionNormalized = normalizeRegion(query.region);

        filtered = allAssignableUsers.filter((user) => {
          if (!user?.canBeAssignee || user?.isActive === false) return false;

          const userRoleNormalized = normalizeRole(user.role);

          if (shouldExcludePeers) {
            if (
              userRoleNormalized === 'CH' ||
              userRoleNormalized === 'CHAIRPERSON' ||
              userRoleNormalized === 'CHAIRMAN' ||
              userRoleNormalized === 'ADMIN'
            ) {
              return false;
            }
          }

          // Filter by query region for CH role
          if (shouldExcludePeers && queryRegionNormalized && queryRegionNormalized !== 'ALL') {
            const userRegionNormalized = normalizeRegion(user.region);
            if (!userRegionNormalized || userRegionNormalized !== queryRegionNormalized) {
              return false;
            }
          }

          return true;
        });
      } else {
        filtered = filterAssignableUsers(allAssignableUsers, currentUser, {
          queryRegion: query.region,
        });
      }
    }

    const currentAssignee = query.assignee;
    if (currentAssignee && currentAssignee._id) {
      const exists = filtered.some((user) => user._id === currentAssignee._id);
      if (!exists) {
        // Only include current assignee if they match the query's region
        const queryRegion = (query.region || '').trim().toUpperCase();
        const assigneeRegion = (currentAssignee.region || '').trim().toUpperCase();
        if (!queryRegion || queryRegion === 'ALL' || !assigneeRegion || assigneeRegion === queryRegion) {
          filtered = [currentAssignee, ...filtered];
        }
      }
    }

    setUsers(
      filtered
        .slice()
        .sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
        )
    );
  }, [allAssignableUsers, currentUser, query]);

  useEffect(() => {
    if (!open) return;
    fetchAssignableUsers();
  }, [open, fetchAssignableUsers]);

  useEffect(() => {
    if (!open) return;
    applyAssignableOptions();
  }, [open, applyAssignableOptions]);

  return {
    users,
    loadingUsers,
  };
};
